import React, { useState } from 'react';
import { ArrowDown, CheckCircle, MapPin, XCircle, Info, PackageCheck, Accessibility, PenLine, ShieldAlert } from 'lucide-react';

// ── Standing order, as the driver reads it ───────────────────────────────────
// One card for two drivers on two rounds, so it is built to be read at a glance at 7am:
// WHERE THE LABEL SAYS (red, crossed) → WHERE IT GOES (green), then one sentence about what
// it means for THIS driver's round. Nothing from the email trail — who asked and when is the
// office's business; the driver needs the two addresses and the rule.
//
// The office's Standing Orders page renders this same component as its "what the driver
// sees" preview (preview=true), so the two can never disagree about the wording.

export const SO_API = '/.netlify/functions/portal-standing-orders';
// The kinds of order this build can draw. The server only sends an app the kinds it names,
// because an older build draws anything it doesn't know as "deliver elsewhere" — and a
// take-special-care address drawn that way tells the driver NOT to deliver there (Paul,
// 29 Oct 2026). A signed-in driver's app does not reload itself on a deploy.
export const SO_KINDS = ['redirect', 'atl', 'care'];
export const soApi = (action, payload) => fetch(SO_API, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, kinds: SO_KINDS, ...payload }),
}).then((r) => r.json());

const short = (a) => (a || '').split(',')[0].trim();

// What this order means for the driver holding the phone. Said as an instruction, never as
// a description of the arrangement — "take them to…", not "parcels are relocated to…".
export function roleLine(o) {
  const from = short(o.from && o.from.address);
  const to = short(o.to && o.to.address);
  const toName = (o.to && o.to.name) || to;
  const desk = /^the\s/i.test(toName) ? toName : `the ${toName}`;
  // The hand-off (Paul, 25 Sep 2026): the driver whose round has the addressed-to building
  // does NOT deliver these — they give them to whoever delivers the deliver-to building,
  // who takes them in. Both sides are named by first name, so each knows who to find.
  const { handTo, handFrom } = handoff(o);
  switch (o.role) {
    case 'leader': {
      const giver = (o.people || []).filter((p) => p.role === 'from');
      const taker = (o.people || []).filter((p) => p.role === 'to' || p.role === 'both');
      const nm = (list) => list.map((p) => `${p.first || p.login} (${p.login})`).join(' and ');
      const how = giver.length && taker.length
        ? `${nm(giver)} gets parcels for ${from} and gives them to ${nm(taker)}, who takes them to ${desk}.`
        : `Parcels addressed to ${from} go to ${desk} at ${to}.`;
      return `Your team: ${how} If you or anyone else covers either round, do the same — never deliver to ${from}.`;
    }
    case 'from':
      if (handTo) return `Parcels addressed to ${from} come to you. Don't deliver them yourself — give them to ${handTo}, who takes them to ${desk}.`;
      return `${from} is on your round. Do NOT deliver there. Take every parcel addressed to it to ${toName} at ${to}.`;
    case 'to':
      if (handFrom) return `${handFrom} gives you the parcels addressed to ${from}. Take them to ${desk} at ${to} — never to ${from}.`;
      return `${to} is on your round. If you have a parcel addressed to ${from}, do not take it there. Deliver it to ${toName} at ${to}.`;
    case 'both':
      return `Both addresses are on your round. Anything addressed to ${from} goes to ${toName} at ${to}.`;
    default:
      return `If you cover this area and have a parcel addressed to ${from}, deliver it to ${toName} at ${to}.`;
  }
}

const joinNames = (n) => (n.length > 1 ? `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}` : (n[0] || ''));
// Who this reader hands to / receives from, by first name. `people` is every driver on the
// order with their first name; the reader's own role says which side they are on.
function handoff(o) {
  const people = o.people || [];
  const firsts = (roles) => people.filter((p) => roles.includes(p.role) && p.login !== o.me).map((p) => p.first || p.login);
  return {
    handTo: o.role === 'from' ? joinNames(firsts(['to', 'both'])) : '',
    handFrom: o.role === 'to' ? joinNames(firsts(['from'])) : '',
  };
}

const fmt = (d) => (d ? new Date(d.length === 10 ? d + 'T00:00:00' : d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

function Place({ tone, label, name, address, struck }) {
  const c = tone === 'red' ? '#ef4444' : '#22c55e';
  return (
    <div style={{ border: `2px solid ${c}`, borderRadius: 10, padding: '10px 12px', background: tone === 'red' ? 'rgba(239,68,68,0.07)' : 'rgba(34,197,94,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: c }}>
        {tone === 'red' ? <XCircle size={13} /> : <CheckCircle size={13} />} {label}
      </div>
      {name && <div style={{ fontSize: '1rem', fontWeight: 800, marginTop: 5 }}>{name}</div>}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: name ? 2 : 5 }}>
        <MapPin size={14} style={{ color: c, flexShrink: 0, marginTop: 3 }} />
        <span style={{ fontSize: '0.95rem', fontWeight: name ? 600 : 800, textDecoration: struck ? 'line-through' : 'none', textDecorationColor: c }}>{address}</span>
      </div>
    </div>
  );
}

// What an ATL means for the person holding the phone. Same rule as roleLine: an instruction.
export function atlLine(o) {
  const where = short(o.from && o.from.address);
  const box = o.receptacle || 'the parcel receptacle at the property';
  const sig = o.include_signature ? ' That includes signature items — do not card them and do not take them back to the Post Office.' : '';
  if (o.role === 'leader') {
    const who = (o.people || []).map((p) => `${p.first || p.login} (${p.login})`).join(', ');
    return `Your team${who ? ` — ${who}` : ''}: every parcel for ${where} is left in ${box}.${sig} Whoever covers the round does the same.`;
  }
  return `Leave every parcel for ${where} in ${box}.${sig}`;
}

// ── Authority to Leave, as the driver reads it ───────────────────────────────
// One green box (where everything goes), the rule in one sentence, and — for a customer
// with a disability — why it matters, in words that make the driver want to get it right.
// A driver confirms it EVERY day (o.daily): it is on their shift screen each morning.
function AtlCard({ o, acked, busy, err, ack }) {
  const pill = (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 5, border: `1px solid ${c}`, color: c });
  return (
    <div style={{ background: 'var(--hp-surface)', border: `${acked ? 1 : 2}px solid ${acked ? 'var(--hp-border)' : '#ef4444'}`, borderRadius: 'var(--radius)', padding: 14, marginBottom: 10, color: 'var(--hp-text)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 9 }}>
        <span style={pill('#22c55e')}><PackageCheck size={12} /> Authority to Leave</span>
        {o.disability && <span style={pill('#60a5fa')}><Accessibility size={12} /> Customer with a disability</span>}
        {o.role === 'leader' && <span style={pill('#f59e0b')}>Your team</span>}
        {acked
          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.7rem', color: '#34d399' }}><CheckCircle size={12} /> {o.daily ? 'confirmed today' : 'you have this'}</span>
          : <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{o.daily ? 'Confirm for today' : 'Please read'}</span>}
      </div>
      {o.customer && <div style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: 9 }}>{o.customer}</div>}

      <Place tone="green" label="Leave ALL parcels here — no card" name={o.from && o.from.name} address={o.from && o.from.address} />
      <div style={{ marginTop: 8, border: '1px dashed #22c55e', borderRadius: 10, padding: '9px 12px', fontSize: '0.92rem', fontWeight: 700, display: 'flex', gap: 7 }}>
        <PackageCheck size={16} style={{ color: '#22c55e', flexShrink: 0, marginTop: 2 }} />
        <span>In: {o.receptacle || 'the parcel receptacle at the property'}</span>
      </div>
      {o.include_signature && (
        <div style={{ marginTop: 8, border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.08)', borderRadius: 10, padding: '9px 12px', fontSize: '0.88rem', fontWeight: 700, display: 'flex', gap: 7 }}>
          <PenLine size={16} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 2 }} />
          <span>Signature items too. Leave them — never card them back to the Post Office.</span>
        </div>
      )}

      <div style={{ marginTop: 11, fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.45 }}>{atlLine(o)}</div>
      {o.disability && (
        <div style={{ marginTop: 6, fontSize: '0.85rem', lineHeight: 1.45, color: 'var(--hp-dim)' }}>
          Australia Post has approved this for a customer who cannot get to the Post Office. A carded parcel is one they cannot collect.
        </div>
      )}
      {o.note && <div style={{ marginTop: 6, fontSize: '0.9rem', lineHeight: 1.45 }}>{o.note}</div>}
      {o.instruction && <div style={{ marginTop: 8, fontSize: '0.9rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{o.instruction}</div>}
      {o.applies_to && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, fontSize: '0.8rem', color: 'var(--hp-dim)', lineHeight: 1.45 }}>
          <Info size={13} style={{ flexShrink: 0, marginTop: 2 }} /><span>{o.applies_to}</span>
        </div>
      )}
      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 10 }}>
        {o.starts_on ? `In place since ${fmt(o.starts_on)}${o.case_number ? ` (AP case ${o.case_number})` : ''}. ` : ''}
        {o.daily
          ? (acked ? 'Confirmed for today. It comes back each morning for as long as it stands.' : 'This comes up every working day — tap below before you head out.')
          : (acked ? `You confirmed this on ${fmt(o.acked_at)}.` : 'It stays on your phone under Standing Orders.')}
      </div>
      {!acked && (
        <button disabled={busy} onClick={ack}
          style={{ width: '100%', marginTop: 11, padding: 13, borderRadius: 8, border: 'none', background: 'var(--hp-green)', color: '#04110b', fontWeight: 800, fontSize: '0.92rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <CheckCircle size={16} /> {o.daily ? `Got it — I leave everything at ${short(o.from && o.from.address)} today` : 'Got it'}
        </button>
      )}
      {err && <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

// ── Take special care, as the driver reads it ────────────────────────────────
// An address where we let the customer down (33 Mitchell St, Glebe — Paul, 29 Oct 2026).
// Nothing about where the parcel goes changes, so there is no "deliver to" box: the address
// and the rule. Red every morning until they tap Got it, like an ATL.
// Deliberately calm and without the customer's name: what went wrong (and any allegation)
// lives in the office notes, never on a driver's phone, where it could be read out of context.
function CareCard({ o, acked, busy, err, ack }) {
  const pill = (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 5, border: `1px solid ${c}`, color: c });
  const where = short(o.from && o.from.address);
  return (
    <div style={{ background: 'var(--hp-surface)', border: `${acked ? 1 : 2}px solid ${acked ? 'var(--hp-border)' : '#ef4444'}`, borderRadius: 'var(--radius)', padding: 14, marginBottom: 10, color: 'var(--hp-text)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 9 }}>
        <span style={pill('#ef4444')}><ShieldAlert size={12} /> Take special care</span>
        {o.role === 'leader' && <span style={pill('#f59e0b')}>Your team</span>}
        {acked
          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.7rem', color: '#34d399' }}><CheckCircle size={12} /> {o.daily ? 'confirmed today' : 'you have this'}</span>
          : <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{o.daily ? 'Confirm for today' : 'Please read'}</span>}
      </div>
      <div style={{ border: '2px solid #ef4444', borderRadius: 10, padding: '10px 12px', background: 'rgba(239,68,68,0.07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#ef4444' }}>
          <ShieldAlert size={13} /> Take extra care at this address
        </div>
        {o.from && o.from.name && <div style={{ fontSize: '1rem', fontWeight: 800, marginTop: 5 }}>{o.from.name}</div>}
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 5 }}>
          <MapPin size={14} style={{ color: '#ef4444', flexShrink: 0, marginTop: 3 }} />
          <span style={{ fontSize: '0.95rem', fontWeight: 800 }}>{o.from && o.from.address}</span>
        </div>
      </div>

      {/* The office's own words are the instruction (required on a care order); the rule line
          only stands in when there are none, and always for a leader, who covers the round. */}
      {o.instruction && <div style={{ marginTop: 11, fontSize: '0.93rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', fontWeight: 600 }}>{o.instruction}</div>}
      {(o.role === 'leader' || !o.instruction) && (
        <div style={{ marginTop: 9, border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.08)', borderRadius: 10, padding: '9px 12px', fontSize: '0.88rem', fontWeight: 700, display: 'flex', gap: 7 }}>
          <PenLine size={16} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 2 }} />
          <span>{o.role === 'leader'
            ? `Your team${(o.people || []).length ? ` — ${(o.people || []).map((p) => `${p.first || p.login} (${p.login})`).join(', ')}` : ''}: whoever delivers ${where} takes extra care — hand parcels over and get the customer's signature, or card them.`
            : `Every parcel for ${where}: hand it over and get the customer's signature, or card it if nobody is home.`}</span>
        </div>
      )}
      {o.note && <div style={{ marginTop: 6, fontSize: '0.9rem', lineHeight: 1.45 }}>{o.note}</div>}
      {o.applies_to && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, fontSize: '0.8rem', color: 'var(--hp-dim)', lineHeight: 1.45 }}>
          <Info size={13} style={{ flexShrink: 0, marginTop: 2 }} /><span>{o.applies_to}</span>
        </div>
      )}
      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 10 }}>
        {o.starts_on ? `Since ${fmt(o.starts_on)}${o.case_number ? ` (AP case ${o.case_number})` : ''}. ` : ''}
        {o.daily
          ? (acked ? 'Confirmed for today. It comes back each morning for as long as it stands.' : 'This comes up every working day — tap below before you head out.')
          : (acked ? `You confirmed this on ${fmt(o.acked_at)}.` : 'It stays on your phone under Standing Orders.')}
      </div>
      {!acked && (
        <button disabled={busy} onClick={ack}
          style={{ width: '100%', marginTop: 11, padding: 13, borderRadius: 8, border: 'none', background: 'var(--hp-green)', color: '#04110b', fontWeight: 800, fontSize: '0.92rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <CheckCircle size={16} /> {o.daily ? `Got it — I take care at ${where} today` : 'Got it'}
        </button>
      )}
      {err && <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

export default function StandingOrderCard({ o, onAcked, preview }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const acked = !!o.acked_at;

  const ack = async () => {
    // The office's preview shows the button the driver sees but must never press it for
    // them — an acknowledgement is a record of what the DRIVER did.
    if (preview) { setErr('Preview only — this is the driver’s button, not yours.'); return; }
    setBusy(true); setErr('');
    try {
      const res = await soApi('ack', { id: o.id });
      if (res && res.success) onAcked && onAcked(o.id, res.acked_at);
      else setErr((res && res.error) || 'Something went wrong');
    } catch (e) { setErr('Network error — try again'); }
    finally { setBusy(false); }
  };

  if (o.kind === 'atl') return <AtlCard o={o} acked={acked} busy={busy} err={err} ack={ack} />;
  if (o.kind === 'care') return <CareCard o={o} acked={acked} busy={busy} err={err} ack={ack} />;

  return (
    <div style={{ background: 'var(--hp-surface)', border: `${acked ? 1 : 2}px solid ${acked ? 'var(--hp-border)' : '#ef4444'}`, borderRadius: 'var(--radius)', padding: 14, marginBottom: 10, color: 'var(--hp-text)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 9 }}>
        <span style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 5, border: '1px solid var(--hp-gold)', color: 'var(--hp-gold)' }}>Standing order</span>
        {o.role === 'leader' && <span style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 5, border: '1px solid #f59e0b', color: '#f59e0b' }}>Your team</span>}
        {acked
          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.7rem', color: '#34d399' }}><CheckCircle size={12} /> you have this</span>
          : <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Please read</span>}
      </div>
      {o.customer && <div style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: 9 }}>{o.customer}</div>}

      <Place tone="red" label="Parcels addressed to — do not deliver here" name={o.from && o.from.name} address={o.from && o.from.address} struck />
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0', color: 'var(--hp-gold)' }}><ArrowDown size={22} strokeWidth={3} /></div>
      <Place tone="green" label={handoff(o).handTo ? `${handoff(o).handTo} delivers them to` : 'Deliver them to'} name={o.to && o.to.name} address={o.to && o.to.address} />

      <div style={{ marginTop: 11, fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.45 }}>{roleLine(o)}</div>
      {o.note && <div style={{ marginTop: 6, fontSize: '0.9rem', lineHeight: 1.45 }}>{o.note}</div>}
      {o.instruction && <div style={{ marginTop: 8, fontSize: '0.9rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{o.instruction}</div>}
      {o.applies_to && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, fontSize: '0.8rem', color: 'var(--hp-dim)', lineHeight: 1.45 }}>
          <Info size={13} style={{ flexShrink: 0, marginTop: 2 }} /><span>{o.applies_to}</span>
        </div>
      )}

      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 10 }}>
        {o.starts_on ? `In place since ${fmt(o.starts_on)}. ` : ''}
        {acked
          ? `You confirmed this on ${fmt(o.acked_at)}. It stays under Standing Orders for as long as it stands.`
          : 'It stays on your phone under Standing Orders — check it any time.'}
      </div>
      {!acked && (
        <button disabled={busy} onClick={ack}
          style={{ width: '100%', marginTop: 11, padding: 13, borderRadius: 8, border: 'none', background: 'var(--hp-green)', color: '#04110b', fontWeight: 800, fontSize: '0.92rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <CheckCircle size={16} /> {handoff(o).handTo ? `Got it — I give them to ${handoff(o).handTo}` : `Got it — they go to ${short(o.to && o.to.address)}`}
        </button>
      )}
      {err && <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: 6 }}>{err}</div>}
    </div>
  );
}
